/**
 * useGraphCheck.ts
 * ─────────────────
 * Hook : vérifie et reconstruit le Legal Graph d'une fiche à la volée.
 *
 * Logique :
 *   1. Dès la fiche chargée → GET /check-graph (silencieux)
 *   2. Si graph absent → auto-rebuild pour tous les utilisateurs
 *   3. Si anomalies fixables → auto-rebuild pour tous les utilisateurs
 *   4. Bouton rebuild manuel toujours visible pour les admins
 */

import { useEffect, useState, useCallback, useRef } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GraphAnomaly {
  type:    string;
  detail:  string;
  count?:  number;
  fixable: boolean;
}

export interface GraphCheckResult {
  fiche_id:             string;
  number:               string;
  graph_built_at:       string | null;
  procedure_graph_size: number;
  anomalies:            GraphAnomaly[];
  can_fix:              boolean;
  checked_at:           string;
}

export type GraphStatus =
  | 'idle'
  | 'checking'
  | 'ok'
  | 'updating'
  | 'rebuilding'
  | 'done'
  | 'error';

interface UseGraphCheckOptions {
  ficheId:       string | null;
  getToken:      () => Promise<string | null>;
  userEmail:     string | null;
  onRebuildDone: () => void;
}

// ── Emails admins (même liste que backend) ────────────────────────────────────

const ADMIN_EMAILS = new Set([
  'raphael@smartlawyer.ai',
  'pierrelouis@smartlawyer.ai',
  'jeremie@smartlawyer.ai',
]);

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useGraphCheck({
  ficheId,
  getToken,
  userEmail,
  onRebuildDone,
}: UseGraphCheckOptions) {
  const [status,       setStatus]       = useState<GraphStatus>('idle');
  const [report,       setReport]       = useState<GraphCheckResult | null>(null);
  const [rebuildError, setRebuildError] = useState<string | null>(null);

  const checkedFor      = useRef<string | null>(null);
  const autoRebuildDone = useRef<string | null>(null); // ficheId pour lequel l'auto-rebuild a été lancé

  const isAdmin = userEmail ? ADMIN_EMAILS.has(userEmail) : false;

  // ── triggerRebuild ────────────────────────────────────────────────────────
  const triggerRebuild = useCallback(async (targetFicheId?: string) => {
    const fid = targetFicheId || ficheId;
    if (!fid) return;

    setStatus('rebuilding');
    setRebuildError(null);

    try {
      const token = await getToken().catch(() => null);
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const resp = await fetch(`/api/fiches/${fid}/rebuild-graph`, {
        method: 'POST',
        headers,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Erreur inconnue' }));
        throw new Error(err.detail || 'Rebuild échoué');
      }

      setStatus('done');
      setTimeout(() => {
        checkedFor.current = null;  // ← permet un nouveau check-graph après rebuild
        onRebuildDone();
        setStatus('ok');
      }, 1500);
    } catch (e: any) {
      setRebuildError(e.message || 'Erreur lors de la reconstruction du Legal Graph');
      setStatus('error');
    }
  }, [ficheId, getToken, onRebuildDone]);

  // ── Check au chargement de la fiche + auto-rebuild si nécessaire ──────────
  useEffect(() => {
    if (!ficheId || checkedFor.current === ficheId) return;
    checkedFor.current = ficheId;

    setStatus('checking');
    setReport(null);
    setRebuildError(null);

    fetch(`/api/fiches/${ficheId}/check-graph`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: GraphCheckResult) => {
        setReport(data);

        const needsRebuild = !data.graph_built_at || data.can_fix;

        if (needsRebuild && autoRebuildDone.current !== ficheId) {
          // Auto-rebuild : graph absent OU anomalies fixables
          autoRebuildDone.current = ficheId;
          setStatus('updating');
          // Lancer directement sans passer par le useEffect secondaire
          // pour éviter les dépendances instables sur triggerRebuild
          const fid = ficheId;
          getToken().catch(() => null).then(token => {
            const headers: Record<string, string> = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            setStatus('rebuilding');
            return fetch(`/api/fiches/${fid}/rebuild-graph`, {
              method: 'POST',
              headers,
            });
          }).then(resp => {
            if (!resp || !resp.ok) throw new Error('Rebuild échoué');
            setStatus('done');
            setTimeout(() => {
              onRebuildDone();
              setStatus('ok');
            }, 1500);
          }).catch((e: any) => {
            setRebuildError(e.message || 'Erreur rebuild');
            setStatus('error');
          });
        } else {
          setStatus(data.anomalies.length > 0 ? 'updating' : 'ok');
        }
      })
      .catch(() => setStatus('idle')); // échec silencieux
  }, [ficheId, getToken, onRebuildDone]);

  return { status, report, isAdmin, triggerRebuild, rebuildError };
}